import type {
  PlanRevisionPlanDiff,
  PlanRevisionPlanDiffEntry,
  PlanRevisionPlanItem,
  PlanRevisionPlanTree,
} from "../types";

import { isRecord, resolvePlanItemId, toStringArray } from "./utils";

type PlanItemPatch = Partial<PlanRevisionPlanItem>;
type PlanItemRecord = Record<string, unknown>;
type NullableStringPatchKey =
  | "description"
  | "implementationDetails"
  | "intent"
  | "key"
  | "sizeSymbol"
  | "status"
  | "title";
type StringArrayPatchKey =
  | "attachmentRefs"
  | "referenceRepositorySlugs"
  | "technicalConstraints"
  | "unitOfWorkIds";

function resolveSlugSource(record: Record<string, unknown>): string | null {
  if (typeof record.repositorySlug === "string") {
    return record.repositorySlug;
  }
  if (typeof record.repository_slug === "string") {
    return record.repository_slug;
  }
  return null;
}

function readNullableString(
  record: PlanItemRecord,
  camelKey: string,
  snakeKey?: string,
): string | null | undefined {
  const camelValue = record[camelKey];
  if (typeof camelValue === "string" || camelValue === null) {
    return camelValue;
  }
  if (!snakeKey) {
    return undefined;
  }
  const snakeValue = record[snakeKey];
  if (typeof snakeValue === "string" || snakeValue === null) {
    return snakeValue;
  }
  return undefined;
}

function readStringArrayField(
  record: PlanItemRecord,
  camelKey: string,
  snakeKey?: string,
): string[] {
  const value =
    snakeKey === undefined
      ? record[camelKey]
      : (record[camelKey] ?? record[snakeKey]);
  return toStringArray(value);
}

function readSelfAssessedRisk(
  record: PlanItemRecord,
): PlanRevisionPlanItem["selfAssessedRisk"] | undefined {
  const value = readNullableString(
    record,
    "selfAssessedRisk",
    "self_assessed_risk",
  );
  if (
    value === "low" ||
    value === "medium" ||
    value === "high" ||
    value === null
  ) {
    return value;
  }
  return undefined;
}

function assignNullableString(
  target: PlanItemPatch,
  key: NullableStringPatchKey,
  value: string | null | undefined,
): void {
  if (value !== undefined) {
    target[key] = value;
  }
}

function assignStringArray(
  target: PlanItemPatch,
  key: StringArrayPatchKey,
  value: string[],
): void {
  if (value.length > 0) {
    target[key] = value;
  }
}

function assignTrimmedRepositorySlug(
  target: PlanItemPatch,
  record: PlanItemRecord,
): void {
  if (!("repositorySlug" in record) && !("repository_slug" in record)) {
    return;
  }
  const slugSource = resolveSlugSource(record);
  if (!slugSource) {
    return;
  }
  const trimmed = slugSource.trim();
  if (trimmed.length > 0) {
    target.repositorySlug = trimmed;
  }
}

function applySharedPlanItemFields(
  target: PlanItemPatch,
  record: PlanItemRecord,
): void {
  assignNullableString(
    target,
    "description",
    readNullableString(record, "description"),
  );
  assignNullableString(
    target,
    "sizeSymbol",
    readNullableString(record, "sizeSymbol", "size_symbol"),
  );
  assignNullableString(
    target,
    "implementationDetails",
    readNullableString(
      record,
      "implementationDetails",
      "implementation_details",
    ),
  );
  assignNullableString(target, "intent", readNullableString(record, "intent"));
  assignNullableString(target, "key", readNullableString(record, "key"));
  assignNullableString(target, "status", readNullableString(record, "status"));
  assignNullableString(target, "title", readNullableString(record, "title"));
  assignStringArray(
    target,
    "attachmentRefs",
    readStringArrayField(record, "attachment_refs"),
  );
  assignStringArray(
    target,
    "referenceRepositorySlugs",
    readStringArrayField(
      record,
      "referenceRepositorySlugs",
      "reference_repository_slugs",
    ),
  );
  assignStringArray(
    target,
    "technicalConstraints",
    readStringArrayField(
      record,
      "technicalConstraints",
      "technical_constraints",
    ),
  );
  assignStringArray(
    target,
    "unitOfWorkIds",
    readStringArrayField(record, "unitOfWorkIds", "uow_ids"),
  );
  const selfAssessedRisk = readSelfAssessedRisk(record);
  if (selfAssessedRisk !== undefined) {
    target.selfAssessedRisk = selfAssessedRisk;
  }
}

export function sanitizePlanItem(value: unknown): PlanRevisionPlanItem | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const id = resolvePlanItemId(record.id, record.key);
  if (!id) {
    return null;
  }
  const slugSource = resolveSlugSource(record);
  const repositorySlug = slugSource?.trim();
  if (!repositorySlug) {
    return null;
  }
  const item: PlanRevisionPlanItem = {
    dependencies: Array.isArray(record.dependencies)
      ? toStringArray(record.dependencies)
      : [],
    id,
    repositorySlug,
  };
  applySharedPlanItemFields(item, record);
  return item;
}

export function sanitizePlanItemPartial(
  value: unknown,
): Partial<PlanRevisionPlanItem> | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const partial: Partial<PlanRevisionPlanItem> = {};
  const id = resolvePlanItemId(record.id, record.key);
  if (id) {
    partial.id = id;
  }
  applySharedPlanItemFields(partial, record);
  if ("dependencies" in record && Array.isArray(record.dependencies)) {
    const deps = toStringArray(record.dependencies);
    if (deps.length > 0) {
      partial.dependencies = deps;
    }
  }
  assignTrimmedRepositorySlug(partial, record);
  return Object.keys(partial).length > 0 ? partial : null;
}

export function sanitizePlanTree(value: unknown): PlanRevisionPlanTree | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const story =
    "story" in record ? (sanitizePlanItem(record.story) ?? null) : null;
  const subtasks = Array.isArray(record.subtasks)
    ? (record.subtasks as unknown[])
        .map((entry: unknown) => sanitizePlanItem(entry))
        .filter((entry): entry is PlanRevisionPlanItem => Boolean(entry))
    : [];
  const selectedRepos = toStringArray(
    record.selectedRepos ?? record.selected_repos,
  );
  const selectedBranches = sanitizeBranchMap(
    record.selectedBranches ?? record.selected_branches,
  );
  return { selectedBranches, selectedRepos, story, subtasks };
}

export function sanitizePlanDiff(value: unknown): PlanRevisionPlanDiff | null {
  if (!isRecord(value)) {
    return null;
  }
  const record = value;
  const added = Array.isArray(record.added) ? toStringArray(record.added) : [];
  const removed = Array.isArray(record.removed)
    ? toStringArray(record.removed)
    : [];
  const updated = Array.isArray(record.updated)
    ? (record.updated as unknown[])
        .map((entry: unknown): PlanRevisionPlanDiffEntry | null => {
          if (!isRecord(entry)) {
            return null;
          }
          const entryRecord = entry;
          const diffId = resolvePlanItemId(entryRecord.id, entryRecord.key);
          if (!diffId) {
            return null;
          }
          const result: PlanRevisionPlanDiffEntry = { id: diffId };
          if ("before" in entryRecord) {
            result.before = sanitizePlanItemPartial(entryRecord.before) ?? null;
          }
          if ("after" in entryRecord) {
            result.after = sanitizePlanItemPartial(entryRecord.after) ?? null;
          }
          return result;
        })
        .filter((entry): entry is PlanRevisionPlanDiffEntry => Boolean(entry))
    : [];
  if (added.length === 0 && removed.length === 0 && updated.length === 0) {
    return null;
  }
  return { added, removed, updated };
}

function sanitizeBranchMap(value: unknown): Record<string, string> {
  if (!isRecord(value)) {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === "string") {
      const trimmed = entry.trim();
      if (trimmed.length > 0) {
        result[key] = trimmed;
      }
    }
  }
  return result;
}
